import { useMemo } from 'react'
import isEmpty from 'lodash/isEmpty'
import type RGL from 'react-grid-layout'
import { DashboardAppSettingKeyEnum } from '@ztechapps/common.contract'
import { useUserSetting } from '@ztechapps/common.hook'

export type DashboardSetting = { layout: RGL.Layout[]; toolbox: RGL.Layout[]; isLocked: boolean }

export type UseDashboardSettingProps = { defaultLayout?: DashboardSetting }

export type UseDashboardSettingResult = {
  setting: DashboardSetting
  loading: boolean
  saving: boolean
  save: (next: Partial<DashboardSetting>) => Promise<void>
}

// What a caller that owns no layout (the profile lock toggle) reads when the user has no saved row.
const emptyLayout: DashboardSetting = { layout: [], toolbox: [], isLocked: true }

/**
 * The one read/write path for the signed-in user's dashboard row. Reading never writes: a user with no row sees
 * `defaultLayout` from memory, and the first `save` creates the row. `save` stores only what the row already holds
 * plus `next`, so a caller without the app's layout (the lock toggle) can flip `isLocked` without clobbering it —
 * missing parts fall back to `defaultLayout` on the next read.
 *
 * `loading` is true only until the row first resolves (`useUserSetting`): the list query re-reports `loading` on
 * the refetch that follows every save, which would otherwise unmount a grid mid-interaction.
 */
export const useDashboardSetting = ({
  defaultLayout = emptyLayout,
}: UseDashboardSettingProps = {}): UseDashboardSettingResult => {
  const { value, loading, saving, save: saveValue } = useUserSetting(DashboardAppSettingKeyEnum.DASHBOARD_LAYOUT)

  const saved = useMemo<Partial<DashboardSetting>>(() => (isEmpty(value) ? {} : JSON.parse(value)), [value])
  const setting = useMemo<DashboardSetting>(() => ({ ...defaultLayout, ...saved }), [defaultLayout, saved])

  const save = (next: Partial<DashboardSetting>) => saveValue(JSON.stringify({ ...saved, ...next }))

  return { setting, loading, saving, save }
}
